import { useEffect } from 'react';

import type {
  ApiABExperimentBucketForId,
  ApiABExperimentId,
} from 'api/abExperiment/types/ApiABExperiment';
import { apiABExperiments } from 'api/abExperiment/types/apiABExperiments';
import { useUserEnvironmentActions } from 'contexts/UserEnvironmentContext';
import { assignAnalyticsAbExperimentBucket } from 'utils/analytics/abExperiments/assignAnalyticsAbExperimentBucket';

import { useInternalABExperimentBucketAssignedValue } from './useInternalABExperimentBucketAssignedValue';

const experimentsDispatched: {
  [bucketId in ApiABExperimentId]: ApiABExperimentBucketForId<bucketId>;
} = {};

/**
 * Returns a bucket for the given `experimentId` if the user environment is ready,
 * otherwise returns the default bucket
 */
export function useAbExperimentBucket<TExperimentId extends ApiABExperimentId>(
  experimentId: TExperimentId,
): [ApiABExperimentBucketForId<TExperimentId>, { isLoading: boolean }] {
  const { setABExperimentBucket } = useUserEnvironmentActions();

  const fallbackValue = {
    type: 'default',
    bucketId: apiABExperiments[experimentId].defaultBucket,
  };

  const value =
    useInternalABExperimentBucketAssignedValue(experimentId) ?? fallbackValue;

  const isAssignedOnFrontend = value.type === 'assigned-on-frontend';
  const { bucketId } = value;

  // Update analytics as soon as possible to make sure we have the latest data instead of `undefined`
  assignAnalyticsAbExperimentBucket(experimentId, bucketId);

  useEffect(() => {
    if (!isAssignedOnFrontend) return;
    if (experimentsDispatched[experimentId] === bucketId) return;

    experimentsDispatched[experimentId] = bucketId;
    setABExperimentBucket(experimentId, bucketId);
  }, [bucketId, experimentId, isAssignedOnFrontend, setABExperimentBucket]);

  return [bucketId, { isLoading: value.type === 'default' }];
}
